import React, { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import { Knob } from "primereact/knob";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import { format } from "date-fns";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import "./Dash.css";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ResponsiveContainer } from "recharts";
import Plot from "react-plotly.js";

function Dash() {
  const [airQuality, setAirQuality] = useState(33);
  const [timeRange, setTimeRange] = useState("day"); // 'day', 'week', or 'month'
  const [currentTime, setCurrentTime] = useState(new Date());
  const [map, setMap] = useState(null);
  const [showLocationDialog, setShowLocationDialog] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationInterval, setAnimationInterval] = useState(null);
  const [animatedAirQuality, setAnimatedAirQuality] = useState(33);
  const [animatedFireData, setAnimatedFireData] = useState([
    {
      name: "Hughes Fire",
      acres: 400,
      coordinates: [-118.6, 34.45],
      color: "#ff4d4d",
      polygonCoordinates: [
        [-118.62, 34.44],
        [-118.62, 34.46],
        [-118.58, 34.46],
        [-118.58, 34.44],
        [-118.62, 34.44],
      ],
    },
    {
      name: "Laguna Fire",
      acres: 200, // Estimated based on NIFC report
      coordinates: [-119.1, 34.2], // Near Camarillo
      color: "#ff8533",
      polygonCoordinates: [
        [-119.12, 34.19],
        [-119.12, 34.21],
        [-119.08, 34.21],
        [-119.08, 34.19],
        [-119.12, 34.19],
      ],
    },
    {
      name: "Eaton Fire",
      acres: 150, // Estimated from fire area
      coordinates: [-118.1, 34.15], // Near Pasadena
      color: "#ffcc00",
      polygonCoordinates: [
        [-118.12, 34.14],
        [-118.12, 34.16],
        [-118.08, 34.16],
        [-118.08, 34.14],
        [-118.12, 34.14],
      ],
    },
    {
      name: "Palisades Fire",
      acres: 300, // Estimated from fire area
      coordinates: [-118.5, 34.05], // Near Malibu
      color: "#ff6b6b",
      polygonCoordinates: [
        [-118.52, 34.04],
        [-118.52, 34.06],
        [-118.48, 34.06],
        [-118.48, 34.04],
        [-118.52, 34.04],
      ],
    },
  ]);

  const getWindDirectionChart = () => {
    const data = {
      labels: ["N", "NE", "E", "SE", "S", "SW", "W", "NW"],
      datasets: [
        {
          label: "Low Velocity (0-5 m/s)",
          data: [10, 15, 25, 35, 30, 20, 15, 10],
          backgroundColor: "rgba(249, 253, 255, 0.7)", // Light Greyish White
          borderColor: "rgba(249, 253, 255, 0.9)",
          borderWidth: 2,
          fill: true,
        },
        {
          label: "Medium Velocity (5-10 m/s)",
          data: [15, 20, 35, 30, 25, 20, 15, 10],
          backgroundColor: "rgba(73, 193, 244, 0.7)", // Light Blue
          borderColor: "rgba(73, 193, 244, 0.9)",
          borderWidth: 2,
          fill: true,
        },
        {
          label: "High Velocity (10-15 m/s)",
          data: [20, 25, 30, 25, 20, 15, 20, 15],
          backgroundColor: "rgba(119, 97, 193, 0.7)", // Purple
          borderColor: "rgba(119, 97, 193, 0.9)",
          borderWidth: 2,
          fill: true,
        },
        {
          label: "Very High Velocity (15+ m/s)",
          data: [26, 35, 35, 45, 30, 25, 25, 35],
          backgroundColor: "rgba(50, 42, 82, 0.7)", // Dark Purple
          borderColor: "rgba(50, 42, 82, 0.9)",
          borderWidth: 2,
          fill: true,
        },
      ],
    };

    const options = {
      scales: {
        r: {
          beginAtZero: true,
          min: 0,
          max: 45,
          ticks: {
            stepSize: 10,
            display: false,
          },
          grid: {
            color: "#e5e7eb",
          },
          pointLabels: {
            font: {
              size: 14,
              weight: "bold",
            },
          },
        },
      },
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          labels: {
            boxWidth: 40,
            padding: 10,
            font: {
              size: 12,
            },
          },
        },
      },
      animation: false,
      transitions: {
        active: {
          animation: {
            duration: 0,
          },
        },
      },
      responsive: true,
      maintainAspectRatio: false,
    };

    return { data, options };
  };

  const [animatedWindData, setAnimatedWindData] = useState(
    getWindDirectionChart().data
  );
  const [animatedChartData, setAnimatedChartData] = useState({
    day: [
      { time: "12P", value: 12 },
      { time: "1P", value: 15 },
      { time: "2P", value: 14 },
      { time: "3P", value: 13 },
      { time: "4P", value: 8 },
      { time: "5P", value: 11 },
      { time: "6P", value: 10 },
      { time: "7P", value: 9 },
      { time: "8P", value: 9 },
      { time: "9P", value: 10 },
      { time: "10P", value: 15 },
      { time: "11P", value: 22 },
      { time: "12A", value: 28 },
      { time: "1A", value: 32 },
    ],
    week: [
      { time: "Mon", value: 18 },
      { time: "Tue", value: 22 },
      { time: "Wed", value: 25 },
      { time: "Thu", value: 20 },
      { time: "Fri", value: 24 },
      { time: "Sat", value: 28 },
      { time: "Sun", value: 23 },
    ],
    month: [
      { time: "Week 1", value: 20 },
      { time: "Week 2", value: 24 },
      { time: "Week 3", value: 22 },
      { time: "Week 4", value: 28 },
    ],
  });

  const requestGeolocation = () => {
    setShowLocationDialog(false);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Success callback - initialize map with user's location
          const initializeMap = new mapboxgl.Map({
            container: "map",
            style: "mapbox://styles/mapbox/streets-v11",
            center: [position.coords.longitude, position.coords.latitude],
            zoom: 10,
          });

          initializeMap.on("load", () => {
            // Add fire markers
            animatedFireData.forEach((fire) => {
              // Create custom fire marker element
              const el = document.createElement("div");
              el.className = "fire-marker";
              el.style.backgroundImage =
                'url(\'data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 3C16.5 10 19.5 12.5 19.5 16.5C19.5 20 16.5 22 12 22C7.5 22 4.5 20 4.5 16.5C4.5 12.5 7.5 10 12 3Z" fill="%23FF4444"/><path d="M12 7C14.5 11.5 16 13 16 15.5C16 17.7 14.2 19 12 19C9.8 19 8 17.7 8 15.5C8 13 9.5 11.5 12 7Z" fill="%23FFA726"/><circle cx="12" cy="15" r="2.5" fill="%23FFEB3B"/></svg>\')';
              el.style.width = "32px";
              el.style.height = "32px";

              new mapboxgl.Marker({
                element: el,
                anchor: "bottom",
              })
                .setLngLat(fire.coordinates)
                .setPopup(
                  new mapboxgl.Popup({ offset: 25 }).setHTML(
                    `<h3 style="font-weight: bold; margin-bottom: 4px;">${fire.name}</h3>
                     <p style="margin: 0;">${fire.acres} acres</p>`
                  )
                )
                .addTo(initializeMap);

              // Add affected area polygon
              initializeMap.addSource(`${fire.name}-polygon`, {
                type: "geojson",
                data: {
                  type: "Feature",
                  geometry: {
                    type: "Polygon",
                    coordinates: [fire.polygonCoordinates],
                  },
                },
              });

              initializeMap.addLayer({
                id: `${fire.name}-polygon`,
                type: "fill",
                source: `${fire.name}-polygon`,
                layout: {},
                paint: {
                  "fill-color": fire.color,
                  "fill-opacity": 0.4,
                  "fill-outline-color": fire.color,
                },
              });
            });
          });

          setMap(initializeMap);
        },
        (error) => {
          console.log("Geolocation error:", error);
          const initializeMap = new mapboxgl.Map({
            container: "map",
            style: "mapbox://styles/mapbox/streets-v11",
            center: [-118.6, 34.2], // Centered between all fires
            zoom: 9, // Zoomed out to show all fires
          });

          // Add fire markers and polygons as before
          initializeMap.on("load", () => {
            animatedFireData.forEach((fire) => {
              // Create custom fire marker element
              const el = document.createElement("div");
              el.className = "fire-marker";
              el.style.backgroundImage =
                'url(\'data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 3C16.5 10 19.5 12.5 19.5 16.5C19.5 20 16.5 22 12 22C7.5 22 4.5 20 4.5 16.5C4.5 12.5 7.5 10 12 3Z" fill="%23FF4444"/><path d="M12 7C14.5 11.5 16 13 16 15.5C16 17.7 14.2 19 12 19C9.8 19 8 17.7 8 15.5C8 13 9.5 11.5 12 7Z" fill="%23FFA726"/><circle cx="12" cy="15" r="2.5" fill="%23FFEB3B"/></svg>\')';
              el.style.width = "32px";
              el.style.height = "32px";

              new mapboxgl.Marker({
                element: el,
                anchor: "bottom",
              })
                .setLngLat(fire.coordinates)
                .setPopup(
                  new mapboxgl.Popup({ offset: 25 }).setHTML(
                    `<h3 style="font-weight: bold; margin-bottom: 4px;">${fire.name}</h3>
                     <p style="margin: 0;">${fire.acres} acres</p>`
                  )
                )
                .addTo(initializeMap);

              // Add affected area polygon
              initializeMap.addSource(`${fire.name}-polygon`, {
                type: "geojson",
                data: {
                  type: "Feature",
                  geometry: {
                    type: "Polygon",
                    coordinates: [fire.polygonCoordinates],
                  },
                },
              });

              initializeMap.addLayer({
                id: `${fire.name}-polygon`,
                type: "fill",
                source: `${fire.name}-polygon`,
                layout: {},
                paint: {
                  "fill-color": fire.color,
                  "fill-opacity": 0.4,
                  "fill-outline-color": fire.color,
                },
              });
            });
          });

          setMap(initializeMap);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        }
      );
    } else {
      // Fallback for browsers that don't support geolocation
      const initializeMap = new mapboxgl.Map({
        container: "map",
        style: "mapbox://styles/mapbox/streets-v11",
        center: [-118.6, 34.2], // Centered between all fires
        zoom: 9, // Zoomed out to show all fires
      });

      // Add fire markers and polygons as before
      initializeMap.on("load", () => {
        animatedFireData.forEach((fire) => {
          // Create custom fire marker element
          const el = document.createElement("div");
          el.className = "fire-marker";
          el.style.backgroundImage =
            'url(\'data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 3C16.5 10 19.5 12.5 19.5 16.5C19.5 20 16.5 22 12 22C7.5 22 4.5 20 4.5 16.5C4.5 12.5 7.5 10 12 3Z" fill="%23FF4444"/><path d="M12 7C14.5 11.5 16 13 16 15.5C16 17.7 14.2 19 12 19C9.8 19 8 17.7 8 15.5C8 13 9.5 11.5 12 7Z" fill="%23FFA726"/><circle cx="12" cy="15" r="2.5" fill="%23FFEB3B"/></svg>\')';
          el.style.width = "32px";
          el.style.height = "32px";

          new mapboxgl.Marker({
            element: el,
            anchor: "bottom",
          })
            .setLngLat(fire.coordinates)
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(
                `<h3 style="font-weight: bold; margin-bottom: 4px;">${fire.name}</h3>
                 <p style="margin: 0;">${fire.acres} acres</p>`
              )
            )
            .addTo(initializeMap);

          // Add affected area polygon
          initializeMap.addSource(`${fire.name}-polygon`, {
            type: "geojson",
            data: {
              type: "Feature",
              geometry: {
                type: "Polygon",
                coordinates: [fire.polygonCoordinates],
              },
            },
          });

          initializeMap.addLayer({
            id: `${fire.name}-polygon`,
            type: "fill",
            source: `${fire.name}-polygon`,
            layout: {},
            paint: {
              "fill-color": fire.color,
              "fill-opacity": 0.4,
              "fill-outline-color": fire.color,
            },
          });
        });
      });

      setMap(initializeMap);
    }
  };

  const declineGeolocation = () => {
    setShowLocationDialog(false);
    // Initialize map with default location
    const initializeMap = new mapboxgl.Map({
      container: "map",
      style: "mapbox://styles/mapbox/streets-v11",
      center: [-118.6, 34.2], // Centered between all fires
      zoom: 8, // Zoomed out to show all fires
    });

    // Add fire markers and polygons as before
    initializeMap.on("load", () => {
      animatedFireData.forEach((fire) => {
        // Create custom fire marker element
        const el = document.createElement("div");
        el.className = "fire-marker";
        el.style.backgroundImage =
          'url(\'data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 3C16.5 10 19.5 12.5 19.5 16.5C19.5 20 16.5 22 12 22C7.5 22 4.5 20 4.5 16.5C4.5 12.5 7.5 10 12 3Z" fill="%23FF4444"/><path d="M12 7C14.5 11.5 16 13 16 15.5C16 17.7 14.2 19 12 19C9.8 19 8 17.7 8 15.5C8 13 9.5 11.5 12 7Z" fill="%23FFA726"/><circle cx="12" cy="15" r="2.5" fill="%23FFEB3B"/></svg>\')';
        el.style.width = "32px";
        el.style.height = "32px";

        new mapboxgl.Marker({
          element: el,
          anchor: "bottom",
        })
          .setLngLat(fire.coordinates)
          .setPopup(
            new mapboxgl.Popup({ offset: 25 }).setHTML(
              `<h3 style="font-weight: bold; margin-bottom: 4px;">${fire.name}</h3>
               <p style="margin: 0;">${fire.acres} acres</p>`
            )
          )
          .addTo(initializeMap);

        // Add affected area polygon
        initializeMap.addSource(`${fire.name}-polygon`, {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: {
              type: "Polygon",
              coordinates: [fire.polygonCoordinates],
            },
          },
        });

        initializeMap.addLayer({
          id: `${fire.name}-polygon`,
          type: "fill",
          source: `${fire.name}-polygon`,
          layout: {},
          paint: {
            "fill-color": fire.color,
            "fill-opacity": 0.4,
            "fill-outline-color": fire.color,
          },
        });
      });
    });

    setMap(initializeMap);
  };

  useEffect(() => {
    mapboxgl.accessToken =
      "pk.eyJ1IjoiZ2VvdGhhcmEiLCJhIjoiY2xrd21tb2VpMTJ0djNybXliZzhwb29hOCJ9.3hVMlLmgoVu8cOPEH5IsYg";

    // Don't initialize map here anymore, wait for user response
    return () => map?.remove();
  }, [animatedFireData]);

  // Add timer to update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute (60000 milliseconds)

    // Cleanup timer on component unmount
    return () => clearInterval(timer);
  }, []);

  // Function to determine color based on air quality value
  const getAirQualityColor = (value) => {
    if (value <= 50) return "#22c55e"; // green-500
    if (value <= 100) return "#eab308"; // yellow-500
    if (value <= 150) return "#f97316"; // orange-500
    if (value <= 200) return "#ef4444"; // red-500
    if (value <= 300) return "#a855f7"; // purple-500
    return "#7f1d1d"; // red-900
  };

  // Function to get status text
  const getAirQualityStatus = (value) => {
    if (value <= 50) return "Good";
    if (value <= 100) return "Moderate";
    if (value <= 150) return "Unhealthy for Sensitive Groups";
    if (value <= 200) return "Unhealthy";
    if (value <= 300) return "Very Unhealthy";
    return "Hazardous";
  };

  // Function to get date range text
  const getDateRangeText = () => {
    switch (timeRange) {
      case "day":
        return `${format(
          new Date(currentTime.getTime() - 24 * 60 * 60 * 1000),
          "EEE, M/d/yy, h:mm a"
        )} to ${format(currentTime, "EEE, M/d/yy, h:mm a")}`;
      case "week":
        return `${format(
          new Date(currentTime.getTime() - 7 * 24 * 60 * 60 * 1000),
          "M/d/yy"
        )} to ${format(currentTime, "M/d/yy")}`;
      case "month":
        return `${format(
          new Date(currentTime.getTime() - 30 * 24 * 60 * 60 * 1000),
          "M/d/yy"
        )} to ${format(currentTime, "M/d/yy")}`;
      default:
        return "";
    }
  };

  const getWindRoseDiagram = () => {
    const data = {
      labels: ["N", "NE", "E", "SE", "S", "SW", "W", "NW"],
      datasets: [
        {
          label: "Ground Level",
          data: [15, 20, 25, 30, 25, 20, 15, 20],
          backgroundColor: "rgba(173, 216, 230, 0.5)", // light blue
          borderColor: "rgba(173, 216, 230, 0.8)",
          borderWidth: 1,
          fill: true,
        },
        {
          label: "50m Height",
          data: [20, 25, 30, 35, 30, 25, 20, 25],
          backgroundColor: "rgba(190, 186, 218, 0.5)", // light purple
          borderColor: "rgba(190, 186, 218, 0.8)",
          borderWidth: 1,
          fill: true,
        },
        {
          label: "100m Height",
          data: [25, 30, 35, 40, 35, 30, 25, 30],
          backgroundColor: "rgba(255, 182, 193, 0.5)", // light pink
          borderColor: "rgba(255, 182, 193, 0.8)",
          borderWidth: 1,
          fill: true,
        },
        {
          label: "200m Height",
          data: [30, 35, 40, 45, 40, 35, 30, 35],
          backgroundColor: "rgba(255, 192, 203, 0.3)", // lighter pink
          borderColor: "rgba(255, 192, 203, 0.6)",
          borderWidth: 1,
          fill: true,
        },
      ],
    };

    const options = {
      scales: {
        r: {
          beginAtZero: true,
          min: 0,
          max: 45,
          ticks: {
            stepSize: 10,
            display: false,
          },
          grid: {
            color: "#e5e7eb",
          },
          pointLabels: {
            font: {
              size: 14,
              weight: "bold",
            },
          },
        },
      },
      plugins: {
        legend: {
          position: "top",
          align: "center",
          labels: {
            boxWidth: 15,
            padding: 15,
          },
        },
      },
      maintainAspectRatio: false,
    };

    return { data, options };
  };

  const startAnimation = () => {
    if (animationInterval) return;

    const interval = setInterval(() => {
      // Animate Air Quality with rounded values
      setAnimatedAirQuality((prev) => {
        const change = Math.random() * 20 - 10;
        return Math.round(Math.min(Math.max(prev + change, 0), 500));
      });

      // Update Wind Rose with very small variations
      setAnimatedWindData((prev) => ({
        labels: prev.labels,
        datasets: prev.datasets.map((dataset) => ({
          ...dataset,
          data: dataset.data.map((value) => {
            // Tiny random changes between -0.5 and +0.5
            const change = Math.random() * 1 - 0.5;
            // Keep values within 5% of original
            const originalValue = value;
            const newValue = Math.max(
              originalValue * 0.95,
              Math.min(originalValue * 1.05, value + change)
            );
            return Math.round(newValue * 10) / 10; // Round to 1 decimal place
          }),
        })),
      }));

      // Update chart data with smaller variations
      setAnimatedChartData((prev) => ({
        day: prev.day.map((point) => ({
          ...point,
          value: Math.max(
            0,
            Math.min(35, point.value + (Math.random() * 2 - 1))
          ), // ±1 change
        })),
        week: prev.week.map((point) => ({
          ...point,
          value: Math.max(
            0,
            Math.min(35, point.value + (Math.random() * 1.5 - 0.75))
          ), // ±0.75 change
        })),
        month: prev.month.map((point) => ({
          ...point,
          value: Math.max(
            0,
            Math.min(35, point.value + (Math.random() * 1 - 0.5))
          ), // ±0.5 change
        })),
      }));
    }, 1500);

    setAnimationInterval(interval);
    setIsAnimating(true);
  };

  const stopAnimation = () => {
    if (animationInterval) {
      clearInterval(animationInterval);
      setAnimationInterval(null);
    }
    setIsAnimating(false);
  };

  // Add CSS transitions for smooth animations
  useEffect(() => {
    // Add CSS to head for smooth transitions
    const style = document.createElement("style");
    style.textContent = `
      .p-knob-value {
        transition: all 1.5s ease-in-out;
      }
      .recharts-line {
        transition: all 1.5s ease-in-out;
      }
      .p-chart {
        transition: all 1.5s ease-in-out;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Create a reusable animation control button component
  const AnimationControl = ({ className }) => (
    <Button
      icon={isAnimating ? "pi pi-pause" : "pi pi-play"}
      onClick={isAnimating ? stopAnimation : startAnimation}
      className={`p-button-rounded p-button-outlined ${className}`}
      aria-label={isAnimating ? "Pause Animation" : "Start Animation"}
    />
  );

  // Add new state for full screen dialog
  const [showFullScreenMap, setShowFullScreenMap] = useState(false);

  // Add function to handle full screen map
  const handleFullScreenMap = () => {
    setShowFullScreenMap(true);
    // Initialize new map in full screen after dialog is shown
    setTimeout(() => {
      const fullScreenMap = new mapboxgl.Map({
        container: "fullscreen-map",
        style: "mapbox://styles/mapbox/streets-v11",
        center: map ? map.getCenter() : [-118.6, 34.2],
        zoom: map ? map.getZoom() : 9,
      });

      // Add fire markers and polygons to full screen map
      fullScreenMap.on("load", () => {
        animatedFireData.forEach((fire) => {
          const el = document.createElement("div");
          el.className = "fire-marker";
          el.style.backgroundImage =
            'url(\'data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 3C16.5 10 19.5 12.5 19.5 16.5C19.5 20 16.5 22 12 22C7.5 22 4.5 20 4.5 16.5C4.5 12.5 7.5 10 12 3Z" fill="%23FF4444"/><path d="M12 7C14.5 11.5 16 13 16 15.5C16 17.7 14.2 19 12 19C9.8 19 8 17.7 8 15.5C8 13 9.5 11.5 12 7Z" fill="%23FFA726"/><circle cx="12" cy="15" r="2.5" fill="%23FFEB3B"/></svg>\')';
          el.style.width = "32px";
          el.style.height = "32px";

          new mapboxgl.Marker({
            element: el,
            anchor: "bottom",
          })
            .setLngLat(fire.coordinates)
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(
                `<h3 style="font-weight: bold; margin-bottom: 4px;">${fire.name}</h3>
                 <p style="margin: 0;">${fire.acres} acres</p>`
              )
            )
            .addTo(fullScreenMap);

          fullScreenMap.addSource(`${fire.name}-polygon-full`, {
            type: "geojson",
            data: {
              type: "Feature",
              geometry: {
                type: "Polygon",
                coordinates: [fire.polygonCoordinates],
              },
            },
          });

          fullScreenMap.addLayer({
            id: `${fire.name}-polygon-full`,
            type: "fill",
            source: `${fire.name}-polygon-full`,
            layout: {},
            paint: {
              "fill-color": fire.color,
              "fill-opacity": 0.4,
              "fill-outline-color": fire.color,
            },
          });
        });
      });
    }, 100);
  };

  // Add new state for 3D wind visualization
  const [show3DWind, setShow3DWind] = useState(false);

  // Add 3D wind data state
  const [wind3DData, setWind3DData] = useState({
    data: [
      // Low Velocity Layer (0-5 m/s)
      {
        type: "scatter3d",
        mode: "lines+markers",
        name: "Low Velocity (0-5 m/s)",
        x: [], // Will be populated with x coordinates
        y: [], // Will be populated with y coordinates
        z: [], // Will be populated with z coordinates
        line: {
          color: "rgba(249, 253, 255, 0.7)",
          width: 4,
        },
        marker: {
          size: 4,
          color: "rgba(249, 253, 255, 0.9)",
        },
      },
      // Medium Velocity Layer (5-10 m/s)
      {
        type: "scatter3d",
        mode: "lines+markers",
        name: "Medium Velocity (5-10 m/s)",
        x: [], // Will be populated with x coordinates
        y: [], // Will be populated with y coordinates
        z: [], // Will be populated with z coordinates
        line: {
          color: "rgba(73, 193, 244, 0.7)",
          width: 4,
        },
        marker: {
          size: 4,
          color: "rgba(73, 193, 244, 0.9)",
        },
      },
      // High Velocity Layer (10-15 m/s)
      {
        type: "scatter3d",
        mode: "lines+markers",
        name: "High Velocity (10-15 m/s)",
        x: [], // Will be populated with x coordinates
        y: [], // Will be populated with y coordinates
        z: [], // Will be populated with z coordinates
        line: {
          color: "rgba(119, 97, 193, 0.7)",
          width: 4,
        },
        marker: {
          size: 4,
          color: "rgba(119, 97, 193, 0.9)",
        },
      },
    ],
    layout: {
      scene: {
        camera: {
          eye: { x: 1.5, y: 1.5, z: 1.5 },
        },
        xaxis: {
          title: "",
          showgrid: true,
          zeroline: false,
          showticklabels: false,
        },
        yaxis: {
          title: "",
          showgrid: true,
          zeroline: false,
          showticklabels: false,
        },
        zaxis: {
          title: "Height (m)",
          range: [0, 200],
        },
        aspectratio: { x: 1, y: 1, z: 0.8 },
      },
      showlegend: true,
      legend: {
        x: 0.8,
        y: 0.9,
      },
      margin: { t: 30, b: 0, l: 0, r: 0 },
      title: "3D Wind Direction Visualization",
    },
    config: {
      responsive: true,
      displayModeBar: true,
    },
  });

  // Add function to handle 3D visualization
  const handle3DWind = () => {
    setShow3DWind(true);

    // Convert wind rose data to 3D coordinates
    setTimeout(() => {
      const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW", "N"];
      const heights = [0, 50, 100, 150, 200];

      const newData = wind3DData.data.map((layer, layerIndex) => {
        const x = [];
        const y = [];
        const z = [];

        // Get the corresponding dataset from wind rose
        const windRoseData = animatedWindData.datasets[layerIndex].data;

        // Create points for each direction at different heights
        directions.forEach((dir, i) => {
          heights.forEach((height) => {
            // Convert polar coordinates to Cartesian
            const angle = (i * Math.PI) / 4; // 8 directions, 45° each
            const radius = windRoseData[i % 8] || 0; // Use wind rose values for radius

            x.push(radius * Math.cos(angle));
            y.push(radius * Math.sin(angle));
            z.push(height);
          });
        });

        return {
          ...layer,
          x,
          y,
          z,
          surfaceaxis: 2, // This makes it render as a surface
          opacity: 0.7,
          showscale: false,
        };
      });

      setWind3DData((prevData) => ({
        ...prevData,
        data: newData,
      }));
    }, 100);
  };

  return (
    <>
      <Dialog
        visible={showLocationDialog}
        onHide={() => declineGeolocation()}
        header="Location Access"
        modal
        className="w-11/12 md:w-[32rem]"
        closeOnEscape={false}
        closable={false}
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <i className="pi pi-map-marker text-2xl text-blue-500"></i>
            <p className="text-lg">
              Allow location access to enhance your experience securely to show
              on map.
            </p>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button
              label="Decline"
              icon="pi pi-times"
              onClick={declineGeolocation}
              className="p-button-text"
            />
            <Button
              label="Allow"
              icon="pi pi-check"
              onClick={requestGeolocation}
              className="p-button-primary"
              autoFocus
            />
          </div>
        </div>
      </Dialog>

      {/* Add Full Screen Map Dialog */}
      <Dialog
        visible={showFullScreenMap}
        onHide={() => setShowFullScreenMap(false)}
        modal
        maximized
        header="Monitoring Locations"
        className="p-0"
      >
        <div id="fullscreen-map" className="w-full h-[calc(100vh-4rem)]"></div>
      </Dialog>

      {/* Add 3D Wind Visualization Dialog */}
      <Dialog
        visible={show3DWind}
        onHide={() => setShow3DWind(false)}
        modal
        maximized
        header="3D Wind Direction Visualization"
        className="p-0"
      >
        <div className="w-full h-[calc(100vh-4rem)] bg-gray-50">
          <Plot
            data={wind3DData.data}
            layout={wind3DData.layout}
            config={wind3DData.config}
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      </Dialog>

      <div className="flex flex-col gap-8 p-4 sm:p-6 bg-gray-100">
        {/* Top Row - Three Equal Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Map Card */}
          <div className="lg:col-span-1">
            <Card
              className="shadow-2xl h-full [&_.p-card-content]:!p-0"
              style={{ padding: 0 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-700">
                  Monitoring Locations
                </h2>
                <Button
                  icon="pi pi-window-maximize"
                  onClick={handleFullScreenMap}
                  className="p-button-rounded p-button-outlined"
                  aria-label="Full Screen"
                  tooltip="Full Screen"
                  tooltipOptions={{ position: "left" }}
                />
              </div>
              <div id="map" className="w-full h-[400px] rounded-lg"></div>
            </Card>
          </div>

          {/* First Cross-sectional Wind Velocity Card */}
          <div className="lg:col-span-1">
            <Card className="shadow-2xl h-full">
              <h2 className="text-xl font-semibold text-gray-700 mb-4">
                3D View of LA Fire
              </h2>
              <div className="h-[400px] relative">
                <spline-viewer
                  url="https://prod.spline.design/EraY-3H2KU0rSBGx/scene.splinecode"
                  className="w-full h-full"
                />
              </div>
            </Card>
          </div>

          {/* Second Cross-sectional Wind Velocity Card */}
          <div className="lg:col-span-1">
            <Card className="shadow-2xl h-full">
              <h2 className="text-xl font-semibold text-gray-700 mb-4">
                Cross-Sectional Wind Velocity
              </h2>
              <div className="h-[400px] relative">
                {/* Background gradient for sky */}
                <div className="absolute inset-0 bg-gradient-to-b from-blue-100 to-blue-50"></div>

                {/* Ground */}
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-b from-green-200 to-green-300"></div>

                {/* Buildings and Structures */}
                <div className="absolute bottom-16 left-0 right-0 flex items-end justify-between px-4">
                  {/* Building */}
                  <div className="relative w-12 h-20 bg-gray-400/50 rounded-t-lg">
                    <div className="absolute -top-6 left-0 right-0 animate-wind-fast">
                      <div className="w-8 h-0.5 bg-gray-400/50 mx-auto mb-1"></div>
                      <div className="w-6 h-0.5 bg-gray-400/50 mx-auto mb-1"></div>
                      <div className="w-4 h-0.5 bg-gray-400/50 mx-auto"></div>
                    </div>
                    <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs font-medium">
                      Bldg 1
                    </span>
                  </div>

                  {/* Small Building 1 */}
                  <div className="relative w-10 h-24 bg-gray-300 rounded-t-sm">
                    <div className="absolute -top-6 left-0 right-0 animate-wind-medium">
                      <div className="w-8 h-0.5 bg-gray-400/50 mx-auto mb-1"></div>
                      <div className="w-6 h-0.5 bg-gray-400/50 mx-auto"></div>
                    </div>
                    <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs font-medium">
                      Bldg 2
                    </span>
                  </div>

                  {/* Big Building */}
                  <div className="relative w-16 h-40 bg-gray-400 rounded-t-sm">
                    <div className="absolute -top-6 left-0 right-0 animate-wind-slow">
                      <div className="w-10 h-0.5 bg-gray-400/50 mx-auto mb-1"></div>
                      <div className="w-8 h-0.5 bg-gray-400/50 mx-auto mb-1"></div>
                      <div className="w-6 h-0.5 bg-gray-400/50 mx-auto"></div>
                    </div>
                    <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs font-medium">
                      Tower
                    </span>
                  </div>

                  {/* Small Building 2 */}
                  <div className="relative w-10 h-28 bg-gray-300 rounded-t-sm">
                    <div className="absolute -top-6 left-0 right-0 animate-wind-medium">
                      <div className="w-8 h-0.5 bg-gray-400/50 mx-auto mb-1"></div>
                      <div className="w-6 h-0.5 bg-gray-400/50 mx-auto"></div>
                    </div>
                    <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs font-medium">
                      Bldg 3
                    </span>
                  </div>
                </div>

                {/* Velocity Indicators */}
                <div className="absolute top-4 left-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <span className="text-xs">High Velocity (15+ m/s)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <span className="text-xs">Medium Velocity (8-15 m/s)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    <span className="text-xs">Low Velocity (0-8 m/s)</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Bottom Row - Three Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Air Quality Card - 25% */}
          <div className="lg:col-span-3">
            <Card className="shadow-2xl h-full">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-700">
                    Air Quality *
                  </h2>
                  <div className="text-sm text-gray-500 mt-1">
                    {format(currentTime, "EEE, MMM d, h:mm a")}
                  </div>
                </div>
                <AnimationControl className="z-10" />
              </div>
              <div className="relative flex flex-col items-center">
                <div className="relative w-full max-w-[250px] mx-auto">
                  <Knob
                    value={Math.round(
                      isAnimating ? animatedAirQuality : airQuality
                    )}
                    onChange={(e) => setAirQuality(e.value)}
                    min={0}
                    max={500}
                    showValue={false}
                    size={Math.min(window.innerWidth * 0.6, 250)}
                    startAngle={180}
                    endAngle={360}
                    valueColor={getAirQualityColor(
                      isAnimating ? animatedAirQuality : airQuality
                    )}
                    rangeColor="#e5e7eb"
                    strokeWidth={12}
                    textColor="#ffffff"
                    valueTemplate={null}
                  />
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                    <div className="text-5xl font-bold mb-1">
                      {isAnimating ? animatedAirQuality : airQuality}
                    </div>
                    <div
                      className="text-xl font-semibold px-3 py-1 rounded-full"
                      style={{
                        color: getAirQualityColor(
                          isAnimating ? animatedAirQuality : airQuality
                        ),
                        backgroundColor: `${getAirQualityColor(
                          isAnimating ? animatedAirQuality : airQuality
                        )}15`,
                      }}
                    >
                      {getAirQualityStatus(
                        isAnimating ? animatedAirQuality : airQuality
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-gray-600 mt-4 text-center text-base">
                  {isAnimating
                    ? animatedAirQuality <= 50
                      ? "No individuals expected to be affected."
                      : "Some individuals may be affected."
                    : airQuality <= 50
                    ? "No individuals expected to be affected."
                    : "Some individuals may be affected."}
                </div>
              </div>

              <div className="mt-8 space-y-2 bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-base mb-3">
                  Air Quality Index
                </h3>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  <span className="text-sm">Good (0-50)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                  <span className="text-sm">Moderate (51-100)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                  <span className="text-sm">
                    Unhealthy for Sensitive Groups (101-150)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500"></span>
                  <span className="text-sm">Unhealthy (151-200)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                  <span className="text-sm">Very Unhealthy (201-300)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-900"></span>
                  <span className="text-sm">Hazardous (301-500)</span>
                </div>
              </div>
            </Card>
          </div>

          {/* Combined LineChart Card - 50% */}
          <div className="lg:col-span-6">
            <Card className="shadow-2xl h-full">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-700">
                    Past{" "}
                    {timeRange === "day"
                      ? "24 Hours"
                      : timeRange === "week"
                      ? "7 Days"
                      : "30 Days"}
                  </h2>
                  <div className="text-sm text-gray-500 mt-1">
                    {getDateRangeText()}
                  </div>
                </div>
                <AnimationControl className="z-10" />
              </div>
              <div className="bg-gray-50 p-4 rounded-lg flex-grow">
                <div className="w-full h-full">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart
                      data={
                        isAnimating
                          ? animatedChartData[timeRange]
                          : animatedChartData[timeRange]
                      }
                      margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="time" stroke="#6b7280" />
                      <YAxis domain={[0, 35]} stroke="#6b7280" />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#00BCD4"
                        strokeWidth={3}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="flex gap-3 mt-4">
                <button
                  className={`px-6 py-2 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                    timeRange === "day"
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  onClick={() => setTimeRange("day")}
                >
                  Day
                </button>
                <button
                  className={`px-6 py-2 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                    timeRange === "week"
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  onClick={() => setTimeRange("week")}
                >
                  Week
                </button>
                <button
                  className={`px-6 py-2 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                    timeRange === "month"
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  onClick={() => setTimeRange("month")}
                >
                  Month
                </button>
              </div>
            </Card>
          </div>

          {/* Wind Rose Diagram - 25% */}
          <div className="lg:col-span-3">
            <Card className="shadow-2xl h-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-700">
                  Wind Direction
                </h2>
                <div className="flex gap-2">
                  <AnimationControl className="z-10" />
                  <Button
                    icon="pi pi-box"
                    onClick={handle3DWind}
                    className="p-button-rounded p-button-outlined"
                    aria-label="Switch to 3D"
                    tooltip="Switch to 3D"
                    tooltipOptions={{ position: "left" }}
                  />
                </div>
              </div>
              <div className="wind-direction-chart">
                <Chart
                  type="radar"
                  data={animatedWindData}
                  options={{
                    ...getWindDirectionChart().options,
                    transitions: {
                      show: {
                        animations: {
                          x: { from: 0 },
                          y: { from: 0 },
                        },
                      },
                    },
                    hide: {
                      animations: {
                        x: { to: 0 },
                        y: { to: 0 },
                      },
                    },
                  }}
                  className="w-full h-[400px]"
                />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}

export default Dash;
